import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Database, RefreshCw, Lock, Package, AlertCircle, List, LayoutGrid } from 'lucide-react';
import { motion } from 'motion/react';
import type { Capsule } from '../types';
import type { CollectClient } from '../lib/api';
import { ApiError } from '../lib/api';
import { isAuthError } from '../lib/connection';
import { capsuleProtection } from '../lib/protection';
import { EmptyState } from './EmptyState';
import { StatusPill } from './StatusPill';
import { WorkspaceBrowser } from './WorkspaceBrowser';

type View = 'timeline' | 'workspaces';

export function CapsulesList({
  client,
  onSelect,
  refreshKey,
  onGoToSettings,
}: {
  client: CollectClient;
  onSelect: (c: Capsule) => void;
  refreshKey: number;
  onGoToSettings: () => void;
}) {
  const [capsules, setCapsules] = useState<Capsule[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(false);
  const [workspaceFilter, setWorkspaceFilter] = useState<string>('');
  const [refresh, setRefresh] = useState(0);
  const [view, setView] = useState<View>(() => {
    const saved = (typeof window !== 'undefined' && localStorage.getItem('collect:capsules-view')) || '';
    return saved === 'workspaces' ? 'workspaces' : 'timeline';
  });

  // Persist view choice
  useEffect(() => {
    try { localStorage.setItem('collect:capsules-view', view); } catch { /* ignore */ }
  }, [view]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    client.listCapsules()
      .then((res) => {
        if (cancelled) return;
        setCapsules(res.capsules);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e : new ApiError('ERROR', e?.message || String(e), 0));
        setCapsules(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [client, refresh, refreshKey]);

  const workspaces = useMemo(() => {
    if (!capsules) return [];
    return Array.from(new Set(capsules.map(c => c.workspace_id))).sort();
  }, [capsules]);

  const filtered = useMemo(() => {
    if (!capsules) return [];
    if (!workspaceFilter) return capsules;
    return capsules.filter(c => c.workspace_id === workspaceFilter);
  }, [capsules, workspaceFilter]);

  if (error) {
    const isAuth = isAuthError(error);
    const title = isAuth ? 'Authentication needed'
      : error.code === 'CORS_BLOCKED' ? 'Server blocks this site'
      : error.code === 'NETWORK' || error.code === 'UNAVAILABLE' ? 'Server unreachable'
      : 'Cannot load capsules';
    const description = isAuth ? 'Open Settings and check your access key, or ask your Qodoonect contact for a new one.'
      : ['CORS_BLOCKED', 'NETWORK', 'NOT_JSON', 'UNAVAILABLE'].includes(error.code) ? error.message
      : `${error.code}: ${error.message}`;
    return (
      <EmptyState
        icon={<AlertCircle size={48} />}
        title={title}
        description={description}
        action={
          <button className="btn btn-primary" onClick={onGoToSettings}>Open Settings</button>
        }
      />
    );
  }

  if (loading && !capsules) {
    return <div className="flex items-center gap-3 justify-center py-20" style={{ color: 'rgba(240,253,244,0.5)' }}>
      <RefreshCw className="animate-spin" size={18} />
      <span>Loading capsules…</span>
    </div>;
  }

  if (capsules && capsules.length === 0) {
    return <EmptyState
      icon={<Database size={48} />}
      title="No capsules yet"
      description="Send your first capsule from the Send tab, or from the CLI: python collect.py send-to-hive my-workspace"
    />;
  }

  // Workspaces view: render the browser with a drill-in callback
  if (view === 'workspaces') {
    return (
      <div>
        <ViewToggle view={view} setView={setView} onRefresh={() => setRefresh(r => r + 1)} loading={loading} />
        <WorkspaceBrowser
          capsules={capsules || []}
          onSelectWorkspace={(ws) => {
            setWorkspaceFilter(ws);
            setView('timeline');
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <ViewToggle view={view} setView={setView} onRefresh={() => setRefresh(r => r + 1)} loading={loading} />

      {/* Hero */}
      <div className="mb-10">
        <h1 className="display text-5xl mb-3">Capsules</h1>
        <p className="text-base" style={{ color: 'rgba(240,253,244,0.6)' }}>
          Memory blocks in your HIVE. Each one carries a workspace's snapshot, hashed (SHA-256) and compressed (gzip).
          Only capsules sent from the CLI with <code className="font-mono" style={{ color: '#34D399' }}>--encrypt</code> are
          encrypted (ML-KEM-768); those show as <span className="font-mono" style={{ color: '#34D399' }}>encrypted</span>.
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-6">
        <select
          className="field max-w-xs"
          value={workspaceFilter}
          onChange={(e) => setWorkspaceFilter(e.target.value)}
        >
          <option value="">All workspaces ({capsules?.length || 0})</option>
          {workspaces.map(w => (
            <option key={w} value={w}>{w}</option>
          ))}
        </select>
        {workspaceFilter && (
          <button className="btn btn-ghost" onClick={() => setWorkspaceFilter('')} title="Clear filter">
            Clear
          </button>
        )}
        <div className="flex-1" />
        <span className="label">{filtered.length} of {capsules?.length || 0}</span>
      </div>

      {/* Timeline / list */}
      <div className="space-y-3">
        {filtered.map((c, i) => {
          const protection = capsuleProtection(c.pqc_algorithm);
          const ProtectionIcon = protection.kind === 'encrypted' ? Lock : Package;
          return (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04, duration: 0.3 }}
          >
            <button
              onClick={() => onSelect(c)}
              className="glass-panel rounded-xl w-full p-5 flex items-center gap-5 text-left group"
            >
              {/* Protection indicator: lock only for a real ML-KEM envelope */}
              <div className="flex-none" title={protection.description}>
                <ProtectionIcon size={20} style={{ color: c.status === 'sealed' ? '#34D399' : '#9CA3AF' }} />
              </div>

              {/* Main col */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="font-mono text-sm font-semibold truncate">{c.name}</span>
                  <StatusPill capsule={c} />
                </div>
                <div className="flex items-center gap-4 text-xs" style={{ color: 'rgba(240,253,244,0.5)' }}>
                  <span className="font-mono">{c.workspace_id}</span>
                  <span>·</span>
                  <span>{formatBytes(c.content_size)}</span>
                  <span>·</span>
                  <span>{formatRelative(c.create_date)}</span>
                  <span>·</span>
                  <span className="font-mono" title={protection.description}>
                    {protection.kind === 'encrypted' ? 'ml-kem-768' : 'gzip · not encrypted'}
                  </span>
                </div>
              </div>

              {/* Hash */}
              <div className="flex-none hidden md:block">
                <div className="hash text-right max-w-[200px] truncate">
                  {c.content_hash.slice(0, 16)}…
                </div>
              </div>

              {/* Chevron */}
              <ChevronRight
                size={18}
                className="flex-none transition-transform group-hover:translate-x-1"
                style={{ color: 'rgba(240,253,244,0.3)' }}
              />
            </button>
          </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function ViewToggle({
  view, setView, onRefresh, loading,
}: {
  view: View;
  setView: (v: View) => void;
  onRefresh: () => void;
  loading: boolean;
}) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="inline-flex rounded-lg p-1" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)' }}>
        <button
          onClick={() => setView('timeline')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all"
          style={{
            background: view === 'timeline' ? 'rgba(16,185,129,0.15)' : 'transparent',
            color: view === 'timeline' ? '#34D399' : 'rgba(240,253,244,0.55)',
          }}
        >
          <List size={13} />
          Timeline
        </button>
        <button
          onClick={() => setView('workspaces')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all"
          style={{
            background: view === 'workspaces' ? 'rgba(16,185,129,0.15)' : 'transparent',
            color: view === 'workspaces' ? '#34D399' : 'rgba(240,253,244,0.55)',
          }}
        >
          <LayoutGrid size={13} />
          Workspaces
        </button>
      </div>
      <div className="flex-1" />
      <button className="btn btn-ghost" onClick={onRefresh} title="Refresh">
        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        Refresh
      </button>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatRelative(iso: string): string {
  const t = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  const diffMs = Date.now() - t.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)}d ago`;
  return t.toISOString().slice(0, 10);
}
